/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';
import { AnimatePresence, motion, type Transition, useReducedMotion } from 'motion/react';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    createEditorDocument,
    createEditorValue,
    isDocumentEmpty,
    trimDocument,
} from '@gitbook/doc-core';
import { Icon } from '@gitbook/spine-icons';
import { Alert, Button, ChatColumn, DropZone, toast } from '@gitbook/spine-react';
import { useEventCallback, useLoadingState, useResizeObserver } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useCurrentOrganizationId } from '../../state';
import {
    type GitBookAgentClient,
    type GitBookAgentClientState,
    GitBookAgentClientStatus,
    type GitBookAgentRejection,
    useAgentInputPrompt,
    useAgentState,
} from '../Agent';
import { decodeDocumentFromAPI } from '../BasicEditor';
import { ErrorScreen } from '../Errors';
import { useAnyImportRunActive } from '../Imports';
import {
    type AgentChatCompiledPrompt,
    AgentChatInput,
    type AgentChatInputValue,
    type SetAgentChatInputValue,
    useAgentChatFiles,
    useAgentChatInputValue,
} from './AgentChatInput';
import { type AgentChatFeedbackInput, AgentChatMessages } from './AgentChatMessages';
import { AgentChatOutbox } from './AgentChatOutbox';
import { AgentChatQuestion } from './AgentChatQuestion';
import { AgentChatQueue } from './AgentChatQueue';
import { AgentContentChangesOverview } from './AgentContentChangesOverview';
import { BackgroundAgentsProvider } from './BackgroundAgentsContext';
import { getAgentEditTarget } from './editTarget';
import { useAnyOpenAPISpecImporting } from './ImportRunCard';
import { usePostAgentMessage } from './usePostAgentMessage';

/**
 * How long a connection may be down before the user is told about it.
 *
 * The socket reconnects on its own, on a backoff doubling from 1s, and every attempt first waits on
 * a fresh token: the first four land within about 15s, and one that hangs is given up on at 20s.
 * Long enough that an outage they recover from is never reported, short enough that a genuinely
 * dead connection is still reported while the user is looking at it.
 */
const CONNECTION_LOST_DELAY = 30_000;

/**
 * Where a message came from: typed into the composer, one of the agent's follow-up suggestions, or
 * a starter offered by the empty state.
 */
export type AgentChatMessageSource = 'input' | 'suggestion' | 'starter';

export type AgentChatSubmitInput = {
    message: api.AIMessageInput;
    messageSource: AgentChatMessageSource;
    messageText: string;
    /**
     * Track message post analytics and usage from the shared submit handler.
     * AgentChat tracks its own submissions, so this is only needed for external entry points.
     */
    trackPostMessage?: boolean;
};

/**
 * What the empty state can do with the conversation it stands in for.
 */
export type AgentChatEmptyStateActions = {
    /**
     * Start the conversation with a prompt, the way the composer runs a template prompt: sent as a
     * message — blocked and delivered exactly as one, and attributed to a starter — or written into
     * the composer for the user to finish. Reporting the pick itself is left to whoever offered it,
     * since only they know what was offered, and where.
     */
    startWith: (prompt: AgentChatCompiledPrompt) => void;
};

type AgentChatClient = Pick<
    GitBookAgentClient,
    | 'postMessage'
    | 'stop'
    | 'listen'
    | 'state'
    | 'reconnect'
    | 'retry'
    | 'retryMessage'
    | 'discardMessage'
    | 'unqueueMessage'
    | 'sendQueuedMessageNow'
>;

export type AgentChatProps = {
    agent: AgentChatClient;
    currentContext?: api.AIMessageContext;
    input?: {
        value: AgentChatInputValue;
        setValue: SetAgentChatInputValue;
        resetValue: () => void;
    };

    /**
     * Persisted key used by the message input state.
     * @default AGENT_INPUT_KEY
     */
    inputKey?: string;

    /**
     * Open the conversation of a background agent the transcript mentions. Left out by a surface
     * with nowhere to open one, which then renders its status without a way in.
     */
    onOpenBackgroundAgent?: (agent: api.AgentBackgroundAgent) => void;

    /**
     * Class name to apply to the chat container.
     */
    className?: string;

    /**
     * If false, the message input will not be displayed.
     * @default true
     */
    withInput?: boolean;

    /**
     * Placeholder for the message input.
     */
    placeholder?: string;

    /**
     * Callback to track when a message is posted.
     */
    onTrackPostMessage?: (input: {
        messageSource: AgentChatMessageSource;
        message: string;
        previousMessagesCount: number;
        messagesSentLast30Days: number;
        attachmentsCount: number;
        imageAttachmentsCount: number;
        fileAttachmentsCount: number;
        importFileAttachmentsCount: number;
    }) => void;
    /**
     * Optional override used to submit a message through an agent-specific path.
     */
    submitMessage?: (input: AgentChatSubmitInput) => Promise<void> | void;
    /**
     * Callback to track when a response is stopped by the user.
     */
    onTrackStopResponse?: (state: GitBookAgentClientState) => void;
    /**
     * Callback to track when a template prompt is picked.
     */
    onTrackPickTemplatePrompt?: (input: { id: string }) => void;
    /**
     * Callback to track when a message is rated.
     */
    onTrackMessageFeedback?: (input: AgentChatFeedbackInput) => void;
    /**
     * If true, the chat is displayed in a floating panel style, with different padding and input styles.
     */
    floating?: boolean;
    /**
     * If true, submissions are intercepted: the input is still cleared but `submitMessage` and
     * `onTrackPostMessage` are not called. `onSubmissionBlocked` is invoked instead so callers
     * can surface a custom block UI (e.g. a usage limit card).
     */
    isSubmissionBlocked?: boolean;
    /**
     * Called when the user attempts to submit while submissions are blocked.
     */
    onSubmissionBlocked?: (input: {
        messageSource: AgentChatMessageSource;
        messageText: string;
    }) => void;
    /**
     * Optional content rendered above the input area (and above the content changes overview).
     * Used to surface contextual banners such as a usage limit warning.
     *
     * Rendered inside a presence boundary: give the element a stable key and an `exit` on its
     * motion root, and its unmount animates. Several banners go in as an array of keyed elements,
     * never wrapped in a fragment — the boundary tracks its direct children, and a fragment is one
     * child whose presence never changes, so nothing inside it would ever animate out.
     */
    bannerAboveInput?: React.ReactNode;
    /**
     * Optional controls rendered in the composer's toolbar, beside the attach and mention controls.
     * Used by a surface that offers context of its own — e.g. the dock, where the change request
     * being viewed can be taken out of the conversation and put back.
     */
    inputToolbarActions?: React.ReactNode;
    /**
     * Optional content rendered in the tray naming the target the agent works in, under its header.
     * For what a surface offers as context of that target with the next message — e.g. the dock's
     * page and selection chips, kept in the one place the conversation's context has been shown
     * since it started.
     */
    editTargetContext?: React.ReactNode;
    /**
     * Optional content rendered inside the scrollable message area, before the first message
     * (e.g. a stack pinned to its top while the conversation scrolls under it).
     */
    messageFlowHeader?: React.ReactNode;
    /**
     * Optional content rendered inside the scrollable message area, after the last message
     * (e.g. surface a usage limit card in place of the conversation).
     */
    messageFlowFooter?: React.ReactNode;
    /**
     * Optional content rendered in the message area while the conversation holds nothing — no
     * message, none on its way — and the composer is empty. It gives way, animated, as soon as the
     * user starts a draft, and comes back if they clear it. Shown only once the agent has sent the
     * conversation over — so a restored one never shows it ahead of its own messages — unless
     * {@link AgentChatProps.startsEmpty} says there is nothing to wait for.
     */
    renderEmptyState?: (actions: AgentChatEmptyStateActions) => React.ReactNode;
    /**
     * Whether the conversation is known to hold nothing: it was opened here and has never been
     * written in. The empty state is then shown without waiting for the agent to send the
     * conversation over — provisioning it, opening a socket and waking a durable object take a
     * moment, and there was never anything in this one to wait for.
     */
    startsEmpty?: boolean;
    /**
     * Next steps offered as pills, ahead of the agent's own suggestions, while the agent is idle
     * and the composer is empty. Picking one sends it as a message.
     */
    nextSteps?: string[];
    /**
     * Options the app offers rather than the agent, shown like the agent's own question at the end
     * of the conversation, while the agent is idle and asks none.
     */
    question?: {
        options: api.AgentQuestionOption[];
        onAnswer: (answer: string) => void;
        loading?: boolean;
    };
};

export const AGENT_INPUT_KEY = 'agentMessage';
/**
 * Chat view for the agent.
 */
export function AgentChat(props: AgentChatProps) {
    return (
        <ErrorScreen>
            <AgentChatContent {...props} />
        </ErrorScreen>
    );
}

function AgentChatContent(props: AgentChatProps) {
    const {
        agent,
        currentContext,
        className,
        placeholder,
        onTrackPostMessage,
        submitMessage = ({ message }) => agent.postMessage(message),
        onTrackStopResponse,
        onTrackPickTemplatePrompt,
        onTrackMessageFeedback,
        withInput = true,
        floating = false,
        input,
        inputKey = AGENT_INPUT_KEY,
        isSubmissionBlocked = false,
        onSubmissionBlocked,
        bannerAboveInput,
        inputToolbarActions,
        editTargetContext,
        messageFlowHeader,
        messageFlowFooter,
        renderEmptyState,
        startsEmpty = false,
        onOpenBackgroundAgent,
        nextSteps,
        question,
    } = props;

    const state = useAgentState(agent);
    const { messages, status, suggestions, pendingQuestion, outbox, queue, consultOnly } = state;
    // Memoized on what the agent reported: rebuilding it every render would defeat the memos
    // the overview keys off it.
    const editTarget = React.useMemo(
        () => getAgentEditTarget(state.editTarget),
        [state.editTarget]
    );

    const prefersReducedMotion = useReducedMotion();

    const internalInput = useAgentChatInputValue(inputKey);
    const { value: message, setValue: setMessage, resetValue } = input ?? internalInput;
    const { uploadTarget: chatUploadTarget, onAddFiles } = useAgentChatFiles(setMessage);

    const organizationId = useCurrentOrganizationId();
    const imports = React.useMemo(() => getImports(messages), [messages]);
    const importingRuns = useAnyImportRunActive({ organizationId, runIds: imports.runIds });
    const importingSpecs = useAnyOpenAPISpecImporting({
        organizationId,
        specSlugs: imports.specSlugs,
    });
    const importing = importingRuns || importingSpecs;

    const onStopResponse = useEventCallback(() => {
        onTrackStopResponse?.(state);
        agent.stop();
    });

    const onRetryMessage = useEventCallback((id: string) => agent.retryMessage(id));
    const onDiscardMessage = useEventCallback((id: string) => agent.discardMessage(id));
    const onSendQueuedNow = useEventCallback((entry: api.AgentQueuedMessage) => {
        // An entry the agent already picked up resolves as done server-side — the message is
        // running either way — so what reaches this toast is a genuine failure to deliver.
        void toast.catch(
            () => agent.sendQueuedMessageNow(entry.id),
            'Could not run this message early'
        );
    });
    const onReturnQueuedToComposer = useEventCallback(async (entry: api.AgentQueuedMessage) => {
        // Restoring would overwrite whatever is drafted, so a filled composer refuses. Same for
        // attachments: the composer holds local files waiting to upload, and these are already
        // uploaded — there is nothing faithful to put back.
        if (!isDocumentEmpty(trimDocument(message.content.document))) {
            toast.error('The composer already has a draft', null, {
                description: 'Send or clear it first.',
            });
            return;
        }
        if (entry.message.attachments?.length) {
            toast.error('This message carries attachments', null, {
                description: 'It can be sent, but not edited back in the composer.',
            });
            return;
        }

        // The server answers with the message even when this client's view of the queue is stale
        // — and refuses when the agent already picked it up, which the toast reports.
        const restored = await toast.catch(
            () => agent.unqueueMessage(entry.id),
            'The message could not be put back'
        );
        if (!restored) {
            return;
        }

        const document =
            typeof restored.content === 'string'
                ? createEditorDocument(restored.content)
                : decodeDocumentFromAPI(restored.content);
        setMessage({
            content: () => {
                const value = createEditorValue(document, 'basic');
                return value.change().collapseToEndOf(value.document).focus().value;
            },
        });
    });
    const onReconnect = useEventCallback(() => agent.reconnect());
    // The refused connection has its own entry point: it clears the refusal and sends what the
    // refusal stranded, neither of which a plain outage recovery should do.
    const onRetryConnection = useEventCallback(() => agent.retry());
    const connectionLost = useConnectionLost(status);
    const errored = status === GitBookAgentClientStatus.ERRORED;
    // Whether the failed turn can be answered from here at all, which is what the banner can offer
    // as the way on: a transcript published to be read has no composer to send from.
    const canReply = withInput && !consultOnly;

    const { postPromptPayload, postSuggestion: onPostSuggestion } = usePostAgentMessage({
        submitMessage,
        currentContext,
        isSubmissionBlocked,
        onSubmissionBlocked,
        onTrackPostMessage,
        getPreviousMessagesCount: () => state.messages.length,
        draftAttachments: message.attachments,
    });

    const setInputWithPrompt = useAgentInputPrompt({ inputKey });
    const startWith = useEventCallback((prompt: AgentChatCompiledPrompt) => {
        if (prompt.kind === 'submit') {
            postPromptPayload(prompt.payload, 'starter');
            return;
        }
        setInputWithPrompt(prompt.payload.content);
    });

    const isConversationEmpty = messages.length === 0 && outbox.length === 0 && queue.length === 0;
    // Only once the agent has sent the conversation over: the connection opens before it has, and
    // until then an empty conversation is one that has not arrived yet — unless the caller knows it
    // was opened here and never written in, which no round trip can add to. Kept through a blip,
    // like the messages are — the agent drops a connection on its first call after hibernating, so
    // moving around with the dock open reconnects routinely, and the last snapshot stands until the
    // next one lands. Withdrawn with the connection once the chat reports it lost: what the agent
    // holds is unknown by then, and the starters would be offered for a conversation another client
    // may have started. Nor once the agent has taken the first message but not yet shown it: the
    // acknowledgment empties the outbox a moment before the append is broadcast, and the welcome
    // would remount into that gap. Not while the composer has given way to an alert either — a
    // starter picked then would go nowhere.
    const showEmptyState =
        renderEmptyState !== undefined &&
        isConversationEmpty &&
        (state.synced || startsEmpty) &&
        !state.awaitingMessages &&
        !connectionLost &&
        !messageFlowFooter &&
        status !== GitBookAgentClientStatus.ERRORED &&
        status !== GitBookAgentClientStatus.REJECTED;
    // A draft in the composer — words or files — is the conversation starting: the empty state
    // gives way to it, and comes back if the draft is cleared.
    const hasDraft =
        !isDocumentEmpty(trimDocument(message.content.document)) || message.attachments.length > 0;
    const isSettled =
        status === GitBookAgentClientStatus.IDLE &&
        outbox.length === 0 &&
        queue.length === 0 &&
        !state.awaitingMessages;
    const visibleNextSteps = isSettled && !hasDraft ? (nextSteps ?? []) : [];
    const appQuestion = isSettled && !pendingQuestion ? question : undefined;
    // The agent's question, or the app's: answered by picking an option or in the composer, both
    // posting a normal message, which the agent resolves its pending question with.
    // Nothing can be sent once the message limit is reached, so neither can an option.
    const questionDisabledReason = isSubmissionBlocked
        ? "You've used all your GitBook Agent messages for this week."
        : undefined;
    const shownQuestion = consultOnly ? null : pendingQuestion ? (
        <AgentChatQuestion
            question={pendingQuestion.question}
            options={pendingQuestion.options}
            disabled={status !== GitBookAgentClientStatus.IDLE}
            disabledReason={questionDisabledReason}
            onSubmit={onPostSuggestion}
        />
    ) : appQuestion ? (
        <AgentChatQuestion
            options={appQuestion.options}
            loading={appQuestion.loading}
            disabledReason={questionDisabledReason}
            onSubmit={appQuestion.onAnswer}
        />
    ) : null;
    // The agent's question is answered with the reply's text alone, so files sent with it would
    // never reach the agent: none can go with the reply until it is answered.
    const uploadTarget = pendingQuestion ? undefined : chatUploadTarget;
    // The turn ends as soon as an import starts: suggesting next steps before it is done reads as
    // if it were.
    const pills = importing
        ? []
        : [
              ...visibleNextSteps,
              ...suggestions.filter((suggestion) => !visibleNextSteps.includes(suggestion)),
          ];
    // Part of the agent's last message when it ends the conversation, so its feedback comes after.
    const questionInLastMessage = messages.at(-1)?.role === api.AIMessageRole.Assistant;
    // A question counts as an offer too, so it scrolls into view when it appears.
    const { scrollRef, onScroll } = useScrollChat(
        state,
        messageFlowFooter,
        pills.length + (shownQuestion ? 1 : 0)
    );
    // Shared with the composer: a submission clears the whole draft once its attachments upload, so
    // a file dropped or pasted meanwhile would be lost.
    const submittingState = useLoadingState();
    const canDropFiles =
        withInput &&
        !consultOnly &&
        !submittingState[0] &&
        status !== GitBookAgentClientStatus.REJECTED &&
        Boolean(uploadTarget);
    const [isDropTarget, setIsDropTarget] = React.useState(false);
    // A drop zone disabled mid-drag never reports the drag leaving, so this can lag behind.
    const showDropTarget = isDropTarget && canDropFiles;
    // Whether the drag over the chat carries files, read from the browser's own marker: the drop
    // zone only sees mime types, which a dragged text selection has too.
    const isFileDragRef = React.useRef(false);

    return (
        // `contents`, so it adds no box: it only reads each drag before the drop zone sees it.
        <div
            className="contents"
            onDragEnterCapture={(event) => {
                isFileDragRef.current = event.dataTransfer.types.includes('Files');
            }}
        >
            <DropZone
                data-testid="agent-chat"
                className={clsx(
                    'relative flex flex-1 flex-col overflow-hidden border transition-colors',
                    showDropTarget ? 'border-neutral-focus' : 'border-transparent',
                    className
                )}
                // Disabled rather than refusing the drop: it also turns off paste, which bypasses
                // the drop operation check.
                isDisabled={!canDropFiles}
                getDropOperation={() => (isFileDragRef.current ? 'copy' : 'cancel')}
                onDropEnter={() => setIsDropTarget(true)}
                onDropExit={() => setIsDropTarget(false)}
                onDrop={async (event) => {
                    setIsDropTarget(false);
                    const files = (
                        await Promise.all(
                            event.items.map((item) =>
                                item.kind === 'file' ? item.getFile() : null
                            )
                        )
                    ).filter(filterOutNullable);
                    if (files.length > 0) {
                        onAddFiles(files);
                    }
                }}
            >
                <div
                    ref={scrollRef}
                    onScroll={onScroll}
                    // Positioned for the empty state's exit, which takes it out of the flow.
                    // Isolated so pinned conversation content stays below app-level overlays.
                    // No room kept for the scrollbar, which would leave more space on the right than on the left.
                    className="relative isolate flex scrollbar min-h-0 flex-1 flex-col gap-3 overflow-auto [scrollbar-gutter:auto]"
                >
                    {/* Popped out of the flow while it leaves, so the first message takes its place at
                    once rather than waiting under it for the fade to finish. */}
                    <AnimatePresence mode="popLayout">
                        {showEmptyState && renderEmptyState ? (
                            <AgentChatEmptyState key="empty-state" hidden={hasDraft}>
                                {renderEmptyState({ startWith })}
                            </AgentChatEmptyState>
                        ) : null}
                    </AnimatePresence>
                    {isConversationEmpty ? null : (
                        <>
                            {messageFlowHeader}
                            <BackgroundAgentsProvider
                                agents={state.backgroundAgents}
                                onOpen={onOpenBackgroundAgent}
                            >
                                <AgentChatMessages
                                    messages={messages}
                                    loading={status === GitBookAgentClientStatus.PROCESSING}
                                    activity={state.activity}
                                    // Like the pills: rating the answer waits for the import.
                                    onTrackMessageFeedback={
                                        importing ? undefined : onTrackMessageFeedback
                                    }
                                    consultOnly={consultOnly}
                                    lastMessageFooter={
                                        questionInLastMessage ? shownQuestion : undefined
                                    }
                                />
                            </BackgroundAgentsProvider>
                            {/* Between the conversation and the outbox, matching their ages: queued
                            entries are acknowledged, an outbox entry is still on its way. */}
                            <AgentChatQueue
                                queue={queue}
                                onSendNow={onSendQueuedNow}
                                onReturnToComposer={onReturnQueuedToComposer}
                            />
                            <AgentChatOutbox
                                outbox={outbox}
                                waitingForConnection={
                                    status === GitBookAgentClientStatus.DISCONNECTED ||
                                    // The first connection is part of sending, not a wait for a
                                    // lost one.
                                    (status === GitBookAgentClientStatus.CONNECTING && state.synced)
                                }
                                isFirstContent={messages.length === 0 && queue.length === 0}
                                onRetry={onRetryMessage}
                                onDiscard={onDiscardMessage}
                            />
                        </>
                    )}

                    {shownQuestion && !questionInLastMessage ? (
                        <ChatColumn className="pb-4">{shownQuestion}</ChatColumn>
                    ) : null}

                    {messageFlowFooter}

                    {!shownQuestion && !messageFlowFooter && pills.length > 0 ? (
                        <ChatColumn className="items-end gap-2 pb-4">
                            <span className="text-ui-small text-muted">Suggested next steps</span>
                            <div className="flex flex-wrap justify-end gap-2">
                                {pills.map((suggestion) => (
                                    <Button
                                        kind="pill"
                                        key={suggestion}
                                        label={suggestion}
                                        size="small"
                                        onPress={() => {
                                            onPostSuggestion(suggestion);
                                        }}
                                    />
                                ))}
                            </div>
                        </ChatColumn>
                    ) : null}
                </div>
                {status === GitBookAgentClientStatus.REJECTED ? (
                    // Refused, not lost: nothing reconnects on its own, and a message typed here would
                    // never go out — so the composer gives way to the reason.
                    <div className="flex">
                        <Alert
                            kind="danger"
                            size="xsmall"
                            details={getRejectionMessage(state.rejection)}
                            action={{ label: 'Try again', onPress: onRetryConnection }}
                            className="m-3"
                        />
                    </div>
                ) : withInput || connectionLost || errored ? (
                    <div className={clsx('flex flex-col', floating ? 'mx-2 mb-2' : 'mx-3 mb-3')}>
                        {connectionLost ? (
                            // The input stays usable while disconnected: submissions are queued in
                            // the outbox and delivered when the connection is back.
                            <div className="flex">
                                <Alert
                                    kind="danger"
                                    size="xsmall"
                                    details="The connection to the agent was lost. Messages you send are kept and delivered once reconnected."
                                    action={{ label: 'Reconnect', onPress: onReconnect }}
                                    className={clsx('flex-1', withInput ? 'mb-3' : 'my-3')}
                                />
                            </div>
                        ) : null}
                        {errored ? (
                            // The turn failed, not the conversation: the agent discards the job it
                            // choked on before it reports the failure, so the next message runs a fresh
                            // one rather than queueing behind a dead one. The composer therefore stays,
                            // above an ordinary banner — replacing it left a failed conversation with no
                            // way on at all, and nothing here recovers one but another message.
                            <div className="flex">
                                <Alert
                                    kind="danger"
                                    size="xsmall"
                                    details={
                                        canReply
                                            ? 'Something went wrong while processing your request. Send another message to try again.'
                                            : 'Something went wrong while processing this request.'
                                    }
                                    className={clsx('flex-1', withInput ? 'mb-3' : 'my-3')}
                                />
                            </div>
                        ) : null}
                        {withInput ? (
                            <>
                                {/* A presence boundary, so a banner being withdrawn — the offer it
                                carried was taken up by the first message — plays its own exit
                                instead of vanishing with the unmount. */}
                                <AnimatePresence initial={false}>
                                    {bannerAboveInput}
                                    {/* Last, so it is the tray touching the composer, and keyed like
                                    the banners so its own slide plays when it unmounts. */}
                                    <AgentContentChangesOverview
                                        key="content-changes"
                                        messages={messages}
                                        loading={status === GitBookAgentClientStatus.PROCESSING}
                                        editTarget={editTarget}
                                        consultOnly={consultOnly}
                                        context={editTargetContext}
                                    />
                                </AnimatePresence>
                                {/* Positioned so a tray tucked behind the composer (via a negative
                                margin) paints underneath it — the composer's surface is opaque,
                                and a positioned box paints above its non-positioned siblings. */}
                                <div className="relative">
                                    {/* A transcript published to be read — the agent produced it on
                                    its own and the run is over — has nothing here to answer. */}
                                    {consultOnly ? null : (
                                        <AgentChatInput
                                            submittingState={submittingState}
                                            value={message}
                                            setValue={setMessage}
                                            uploadTarget={uploadTarget}
                                            onAddFiles={onAddFiles}
                                            onSubmit={(attachments) => {
                                                postPromptPayload({
                                                    content: message.content.document,
                                                    attachments,
                                                });
                                                resetValue();
                                            }}
                                            onSubmitTemplatePrompt={(template) =>
                                                postPromptPayload(template.payload)
                                            }
                                            // Submitting is safe in every state that renders the
                                            // composer: while disconnected or connecting the message
                                            // waits in the outbox, while the agent works it stacks in
                                            // the queue for when the response finishes, and after a
                                            // failed turn it runs a fresh one — the job that failed was
                                            // discarded, so nothing is left to block it.
                                            onStop={
                                                status === GitBookAgentClientStatus.PROCESSING
                                                    ? onStopResponse
                                                    : undefined
                                            }
                                            onPickTemplatePrompt={onTrackPickTemplatePrompt}
                                            placeholder={placeholder}
                                            floating={floating}
                                            inputKey={inputKey}
                                            hasSelection={Boolean(
                                                currentContext?.selection?.content
                                            )}
                                            toolbarActions={inputToolbarActions}
                                        ></AgentChatInput>
                                    )}
                                </div>
                            </>
                        ) : null}
                    </div>
                ) : null}
                <AnimatePresence>
                    {showDropTarget ? (
                        <motion.div
                            className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-neutral-surface-subtle text-neutral-high"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: prefersReducedMotion ? 0 : 0.16,
                                ease: 'easeOut',
                            }}
                        >
                            <div className="flex size-10 items-center justify-center rounded-lg border border-neutral-subtle bg-base text-muted">
                                <Icon.Files className="size-5" />
                            </div>
                            <span className="text-ui-base">Attach files</span>
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </DropZone>
        </div>
    );
}

function isNearBottom(el: HTMLDivElement) {
    const threshold = 24; // px tolerance for "near bottom"
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distance <= threshold;
}

/**
 * Hook to manage auto-scrolling behavior in the chat.
 */
function useScrollChat(
    state: GitBookAgentClientState,
    messageFlowFooter: React.ReactNode,
    pillCount: number
) {
    const disableAutoScroll = React.useRef(false);
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    // Whether the user was at the bottom when they last scrolled, or the chat scrolled itself.
    const atBottom = React.useRef(true);

    // Stay at the bottom when the room above the composer shrinks, as when the edit target's tray
    // loads after the chat has scrolled down, which would hide the last of the conversation.
    const observeScroll = useResizeObserver(
        () => {
            const el = scrollRef.current;
            if (el && atBottom.current && !disableAutoScroll.current) {
                el.scrollTop = el.scrollHeight;
            }
        },
        (element) => {
            scrollRef.current = element as HTMLDivElement;
        }
    );

    // Reset things when the AI finished writing. Before the scroll below, so the turn's end
    // scrolls down.
    React.useEffect(() => {
        if (state.status === GitBookAgentClientStatus.IDLE) {
            disableAutoScroll.current = false;
        }
    }, [state.status]);

    // When messages are updated, the footer is mounted/unmounted or the pills change, scroll to
    // the bottom. The pills can come after the turn ends, once its imports are done. Whether there
    // is a footer, not the element: a surface builds a new one on each render, as when typing.
    const hasFooter = Boolean(messageFlowFooter);
    React.useEffect(() => {
        const hasScrollableContent = state.messages.length > 0 || hasFooter;
        if (scrollRef.current && hasScrollableContent && !disableAutoScroll.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [state, hasFooter, pillCount]);

    // We only disable auto-scroll if they move away from the bottom while the AI is writing.
    const onScroll = useEventCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        atBottom.current = isNearBottom(el);
        disableAutoScroll.current =
            state.status === GitBookAgentClientStatus.PROCESSING && !isNearBottom(el);
    });

    return {
        scrollRef: observeScroll,
        onScroll,
    };
}

/**
 * Whether to report the connection as lost.
 *
 * The socket closing is routine — a sleeping tab, a hibernating durable object, a network blip —
 * and the client reconnects on its own, usually within a moment. Reporting it the instant it
 * happens turns a recovery nobody needed to know about into an alert that flashes every time a
 * message is sent after a pause. Nothing is hidden by waiting: the composer stays usable while
 * disconnected and the message is queued in the outbox either way, so the alert is a diagnosis
 * rather than something to act on.
 */
function useConnectionLost(status: GitBookAgentClientStatus): boolean {
    const connected =
        status !== GitBookAgentClientStatus.DISCONNECTED &&
        status !== GitBookAgentClientStatus.CONNECTING;
    const [outageReported, setOutageReported] = React.useState(false);

    React.useEffect(() => {
        if (connected) {
            // Cleared so the next outage gets the full delay again rather than being reported at
            // once. Nothing reads this while connected — the value below does not depend on it.
            setOutageReported(false);
            return;
        }

        // Reported only once the reconnection has had time to fail — the timer is what separates a
        // blip from an outage, so it restarts whenever the connection comes back.
        const timeout = setTimeout(() => setOutageReported(true), CONNECTION_LOST_DELAY);
        return () => clearTimeout(timeout);
    }, [connected]);

    // Combined during render rather than read off the timer alone: a reconnection has to clear the
    // alert in the same render that reports it, not one render later once the effect above has run.
    return !connected && outageReported;
}

/**
 * What to tell the user about a connection the agent refused, from the status it refused it with.
 */
function getRejectionMessage(rejection: GitBookAgentRejection | null): string {
    switch (rejection?.statusCode) {
        case 401:
        case 403:
            return 'You do not have access to this content.';
        case 404:
            return 'This conversation is not available: it may have been deleted, or belong to someone else.';
        default:
            // The server's own message, which it truncates to fit the close frame; it is the only
            // thing that can say anything specific about a refusal we have no copy for.
            return rejection?.reason
                ? `The agent refused the connection: ${rejection.reason}`
                : 'The agent refused the connection.';
    }
}

/**
 * The frame the empty state animates in: rising into place when there is nothing to read, giving
 * way — faded, and out of reach — while the composer holds a draft, and leaving the same way when
 * the first message arrives.
 */
function AgentChatEmptyState(props: {
    ref?: React.Ref<HTMLDivElement>;
    /** Faded out and made inert rather than unmounted, so clearing the draft brings it back. */
    hidden: boolean;
    children: React.ReactNode;
}) {
    const { ref, hidden, children } = props;
    const prefersReducedMotion = useReducedMotion();
    const transition: Transition = prefersReducedMotion
        ? { duration: 0 }
        : { duration: 0.2, ease: 'easeOut' };

    return (
        <motion.div
            ref={ref}
            className="flex min-h-0 flex-1 flex-col"
            initial="hidden"
            animate={hidden ? 'hidden' : 'visible'}
            exit="hidden"
            variants={{
                visible: { opacity: 1, scale: 1 },
                hidden: { opacity: 0, scale: 0.98 },
            }}
            transition={transition}
            inert={hidden}
        >
            {children}
        </motion.div>
    );
}

/**
 * The imports the agent started in this conversation: content import runs, and the OpenAPI specs
 * it added.
 */
function getImports(messages: api.AIMessage[]): { runIds: string[]; specSlugs: string[] } {
    const toolCalls = messages.flatMap((message) =>
        message.steps.flatMap((step) => step.toolCalls ?? [])
    );
    return {
        runIds: toolCalls.flatMap((toolCall) =>
            toolCall.tool === 'startContentImport' ? [toolCall.runId] : []
        ),
        specSlugs: toolCalls.flatMap((toolCall) =>
            toolCall.tool === 'importOpenAPISpec' ? [toolCall.specSlug] : []
        ),
    };
}
